import {
  ChangeEvent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Layout } from "components/Layout";
import { Button, ButtonVariant } from "components/Form/Button";
import { UploadContext, UploadType } from "contexts/UploadContext";
import { useNavigate, useLocation } from "react-router-dom";
import { AppRoutes } from "types";
import { GalleryPreview } from "components/GalleryPreview";
import { Explanation, ExplanationIcons } from "components/Explanation";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { toast } from "react-toastify";
import { PhotoSourcePicker } from "components/PhotoSourcePicker";
import { getAcceptedFileTypes } from "lib/upload-files";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

export const Capture = memo(function Capture() {
  const [title, setTitle] = useState<string>("");
  const [init, setInit] = useState<boolean>(false);
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);

  const location = useLocation();

  const taskId = location.state?.taskId;
  const taskName = location.state?.taskName;
  const captureType = location.state?.captureType;

  const {
    files: images,
    addFile: addImage,
    resetFiles: resetImages,
    upload,
    checkForDuplicates,
  } = useContext(UploadContext);
  const privacyModal = usePrivacyModal();
  const { setIsShow, config } = useContext(CollectionDetailsContext);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const navigate = useNavigate();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) {
        return;
      }

      const file = e.target.files?.[0];
      // NOTE: resets input state, if we don't do this, this event never fires again
      e.target.value = "";
      addImage(file);
    },
    [addImage]
  );

  const captureButtonLabel = useMemo(() => {
    if (images.length === 0) {
      return "Start capture";
    }

    return "Submit";
  }, [images.length]);

  const handleCancel = useCallback(() => {
    resetImages();
    navigate(AppRoutes.home, { replace: true });
  }, [navigate, resetImages]);

  useEffect(() => {
    if (!config) {
      return;
    }

    if (images.length <= 0) {
      return setTitle(taskName ? `${taskName}` : "Face capture");
    }

    setTitle("Preview images");
  }, [config, images.length, taskName]);

  useEffect(() => {
    if (!config || privacyModal.accepted === null) {
      return;
    }

    if (!taskId) {
      navigate(AppRoutes.home);
      return;
    }

    setInit(true);
  }, [config, navigate, privacyModal.accepted, taskId]);

  // Check for duplicates when images change (preview screen only)
  useEffect(() => {
    if (images.length > 0) {
      const duplicates = checkForDuplicates();
      const hasDups = duplicates.length > 0;
      setHasDuplicates(hasDups);

      if (hasDups) {
        toast.warn(`Some files were not added due to errors: duplication.`);
      }
    } else {
      setHasDuplicates(false);
    }
  }, [images, checkForDuplicates]);

  if (!init || !config) {
    return null;
  }

  return (
    <Layout
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          {images.length === 0 && captureType === "photo" && isIOS && (
            <label className="bg-3461fd text-white hover:bg-3461fd/80 text-center p-4.5 rounded-xl cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes("photo")}
                className="hidden"
                onChange={handleChange}
              />
              Take Photo or Choose from Gallery
            </label>
          )}

          {images.length === 0 && captureType === "photo" && !isIOS && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes("photo")}
                capture="environment"
                className="hidden"
                onChange={handleChange}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept={getAcceptedFileTypes("photo")}
                className="hidden"
                onChange={handleChange}
              />
              <button
                className="bg-3461fd text-white hover:bg-3461fd/80 text-center p-4.5 rounded-xl cursor-pointer w-full"
                onClick={() => setShowPhotoPicker(true)}
              >
                Take Photo or Choose from Gallery
              </button>
            </>
          )}

          {images.length === 0 && captureType !== "photo" && (
            <label className="bg-3461fd text-white hover:bg-3461fd/80 text-center p-4.5 rounded-xl cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes("photo")}
                className="hidden"
                onChange={handleChange}
              />
              {captureButtonLabel}
            </label>
          )}

          {images.length > 0 && (
            <>
              <Button
                onClick={() => upload(UploadType.REGULAR, taskId)}
                disabled={hasDuplicates}
              >
                Submit
              </Button>
              <Button
                variant={ButtonVariant.FLAT}
                onClick={() => {
                  resetImages();
                  setIsShow(true);
                }}
              >
                Change selection
              </Button>
            </>
          )}

          {images.length === 0 && (
            <button
              onClick={handleCancel}
              className="text-sm text-858494 underline hover:text-191c20 transition-colors"
            >
              Back to Home
            </button>
          )}
        </>
      }
    >
      <h1 className="text-24 font-semibold text-center">{title}</h1>

      {images.length > 0 && (
        <div className="text-858494 leading-5 text-center">
          Review images, videos, and labels carefully. <br />
          Correct any mismatches before uploading.
        </div>
      )}

      {images.length <= 0 && (
        <Explanation
          items={[
            {
              icon: ExplanationIcons.selfie,
              title: `Take selfie(s)`,
              description: (
                <p>
                  Tap on the capture button to save the picture. Tap retake to
                  start over.
                </p>
              ),
            },
            {
              icon: ExplanationIcons.bulb,
              title: "Recommendations",
              description: (
                <ul className="leading-[18px] list-disc">
                  <li>Make sure there's some lighting around.</li>
                  <li>Use a neutral expression.</li>
                  <li>
                    Take the picture so your shoulders and above are visible.
                  </li>
                </ul>
              ),
            },
          ]}
        />
      )}

      {images.length > 0 && (
        <GalleryPreview
          showLabels={
            config?.labels &&
            Object.keys(config.labels).some((key) => key !== "devices")
          }
          isRemovable
        />
      )}

      <PhotoSourcePicker
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onCamera={() => {
          setShowPhotoPicker(false);
          fileInputRef.current?.click();
        }}
        onGallery={() => {
          setShowPhotoPicker(false);
          galleryInputRef.current?.click();
        }}
      />
    </Layout>
  );
});
